import React, { useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EnviarGrupoDTO, EventAttendeeDTO, EventDetailDTO, EventGroupDTO, InvitationTextDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { blobErrorText, dinero, errorText, guardarBlob } from "../workshops/comun";
import { cuando } from "./cuando";
import { BotonWhatsappAlumno, useVeWappChat } from "../../../../components/whatsapp/whatsappAlumno";
import EnvioMasivo from "../../../../components/whatsapp/envioMasivo";
import useEsTelefono from "../../../../utils/esTelefono";

interface Props { campusId: number; evento: EventDetailDTO; notify: (t: string, ok?: boolean) => void; }

const slug = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");

/** Resultado de "Enviar Ahora" por canal: "WhatsApp enviado · Correo enviado" o el motivo. */
const textoEnvio = (r: EnviarGrupoDTO): { texto: string; ok: boolean } => {
	const wa = r.whatsapp === "ok" ? S.waEnviado : S.waError(r.whatsapp);
	const mail = r.email === "ok" ? S.correoEnviado : r.email === "sin correo" ? S.sinCorreo : S.correoError(r.email);
	return { texto: wa + " · " + mail, ok: r.whatsapp === "ok" && (r.email === "ok" || r.email === "sin correo") };
};

/** Grupos (fase 2): una invitación por graduada con sus invitados; se manda cuando ya pagó todo. */
const GruposInvitacion: React.FC<{ campusId: number; evento: EventDetailDTO; movil: boolean; notify: (t: string, ok?: boolean) => void }> = ({ campusId, evento, movil, notify }) => {
	const http = HttpManager.getInstance();
	const [grupos, setGrupos] = useState<EventGroupDTO[] | null>(null);
	const [enviando, setEnviando] = useState<number>(0);
	const [res, setRes] = useState<{ [host: number]: { texto: string; ok: boolean } }>({});
	const [vuelta, setVuelta] = useState<number>(0);

	useEffect(() => {
		let vigente = true;
		http.eventsService.grupos(campusId, evento.id)
			.then(r => { if (vigente) setGrupos(Array.isArray(r.data) ? r.data : []); })
			.catch(e => { if (vigente) { setGrupos([]); notify(errorText(e), false); } });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, evento.id, evento.attendees.length, vuelta]);

	if (!grupos || grupos.length === 0) return null;
	const m = (v: number) => dinero(evento.currency, v);

	const copiar = async (g: EventGroupDTO) => {
		try { await navigator.clipboard.writeText(g.url); notify(S.copiado); } catch { /* sin portapapeles: queda Abrir */ }
	};
	const enviar = (g: EventGroupDTO) => {
		setEnviando(g.hostAttendeeId);
		http.eventsService.enviarGrupo(campusId, g.hostAttendeeId)
			.then(r => { const t = textoEnvio(r.data); setRes(x => ({ ...x, [g.hostAttendeeId]: t })); notify(t.texto, t.ok); setVuelta(v => v + 1); })
			.catch(e => setRes(x => ({ ...x, [g.hostAttendeeId]: { texto: errorText(e), ok: false } })))
			.finally(() => setEnviando(0));
	};

	const personas = (g: EventGroupDTO) => <>{S.nPersonas(g.members)}{g.pendingNames > 0 ? <span className="wau-oro"> · {S.faltanNombres(g.pendingNames)}</span> : null}</>;
	const pago = (g: EventGroupDTO) => g.fullyPaid ? <span className="wau-sec">{S.pagadoCompleto}</span> : <span className="wau-oro wau-num">{S.faltaMonto(m(g.pendingAmount))}</span>;
	const envio = (g: EventGroupDTO) => {
		const r = res[g.hostAttendeeId];
		const via = (g.sentVia || []).map(v => S.via[v] || v).join(S.y);
		return <>
			<div className={g.sentAt ? undefined : "wau-sec"}>{g.sentAt ? S.enviada(cuando(g.sentAt), via) : S.sinEnviar}</div>
			{r ? <div className={r.ok ? "wau-sec" : "wau-oro"} style={{ fontSize: 12.5 }}>{r.texto}</div> : null}
		</>;
	};
	const botones = (g: EventGroupDTO) => <>
		<button type="button" className="wau-btn s mini" onClick={() => copiar(g)}>{S.copiarEnlace}</button>
		<a className="wau-btn s mini" href={g.url} target="_blank" rel="noreferrer">{S.abrir}</a>
		<button type="button" className="wau-btn p mini" disabled={!g.fullyPaid || enviando !== 0} onClick={() => enviar(g)}>{enviando === g.hostAttendeeId ? S.enviando : S.enviarAhora}</button>
	</>;

	return (
		<div className="wau-caja">
			<h3 style={{ margin: "0 0 10px 0" }}>{S.grupos} <span className="wau-sec wau-num">({grupos.length})</span></h3>
			{movil ? grupos.map(g => (
				<div key={g.hostAttendeeId} className="wau-tarjeta">
					<b>{g.name}</b>
					<div className="linea">{personas(g)}</div>
					<div style={{ marginTop: 6 }}>{pago(g)}</div>
					<div style={{ marginTop: 6, fontSize: 13.5 }}>{envio(g)}</div>
					<div className="wau-acciones" style={{ justifyContent: "flex-start", flexWrap: "wrap", marginTop: 8 }}>{botones(g)}</div>
				</div>
			)) : (
				<div className="wau-tabla">
					<table>
						<thead><tr><th>{S.colGrupo}</th><th>{S.colPago}</th><th>{S.colEnvio}</th><th></th></tr></thead>
						<tbody>{grupos.map(g => (
							<tr key={g.hostAttendeeId}>
								<td><b>{g.name}</b><div className="wau-sec" style={{ fontSize: 12.5 }}>{personas(g)}</div></td>
								<td style={{ whiteSpace: "nowrap" }}>{pago(g)}</td>
								<td style={{ fontSize: 13.5 }}>{envio(g)}</td>
								<td><div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>{botones(g)}</div></td>
							</tr>
						))}</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

/**
 * Invitaciones (R2): PDF con todas (una página por asistente), WhatsApp por persona con el borrador y el enlace público
 * firmado a su invitación, y "WhatsApp a N" con EnvioMasivo (plantilla) para las elegidas con alumna.
 */
const InvitacionesTab: React.FC<Props> = ({ campusId, evento, notify }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const veWa = useVeWappChat();
	const vigentes = evento.attendees.filter(a => a.status === 1);
	const [textos, setTextos] = useState<{ [attendeeId: number]: InvitationTextDTO } | null>(null);
	const [sel, setSel] = useState<number[]>([]);
	const [masivo, setMasivo] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);

	useEffect(() => {
		let vigente = true;
		setTextos(null);
		http.eventsService.invitationTexts(campusId, evento.id)
			.then(r => { if (!vigente) return; const m: { [k: number]: InvitationTextDTO } = {}; (r.data?.rows || []).forEach(t => { if (t.attendeeId) m[t.attendeeId] = t; }); setTextos(m); })
			.catch(e => { if (vigente) { setTextos({}); notify(errorText(e), false); } });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, evento.id, evento.attendees.length]);

	const conAlumna = vigentes.filter(a => a.studentId);
	const todas = conAlumna.length > 0 && conAlumna.every(a => sel.includes(a.id));
	const elegidas = conAlumna.filter(a => sel.includes(a.id));
	const studentIds = Array.from(new Set(elegidas.map(a => a.studentId as number)));

	const pdfTodas = () => {
		setBusy(true);
		http.eventsService.invitationsPdf(campusId, evento.id)
			.then(r => guardarBlob(r.data, `Invitaciones_${slug(evento.name)}.pdf`))
			.catch(async e => notify(await blobErrorText(e), false))
			.finally(() => setBusy(false));
	};
	const copiar = async (t: InvitationTextDTO) => {
		try { await navigator.clipboard.writeText(t.url); notify(S.copiado); } catch { /* sin portapapeles: el enlace queda a la vista */ }
	};
	const whatsapp = (a: EventAttendeeDTO) => {
		const t = textos?.[a.id];
		if (a.studentId) return <BotonWhatsappAlumno campusId={campusId} studentId={a.studentId} nombre={a.name} texto sugerido={t?.text || null} />;
		if (a.phone) return <a className="wau-btn s mini" href={`https://wa.me/${a.phone.replace(/\D/g, "")}${t ? "?text=" + encodeURIComponent(t.text) : ""}`} target="_blank" rel="noreferrer">{S.colWhatsapp}</a>;
		return <span className="wau-sec">{S.sinTelefono}</span>;
	};
	const enlace = (a: EventAttendeeDTO) => {
		const t = textos?.[a.id];
		if (!t) return <span className="wau-sec">—</span>;
		return <button type="button" className="wau-btn s mini" onClick={() => copiar(t)}>{S.copiar}</button>;
	};

	return (
		<>
		<GruposInvitacion campusId={campusId} evento={evento} movil={movil} notify={notify} />
		<div className="wau-caja">
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
				<h3 style={{ margin: 0 }}>{S.tabs.invitaciones} <span className="wau-sec wau-num">({S.vigentes(vigentes.length)})</span></h3>
				<div className="wau-acciones" style={{ margin: 0 }}>
					{sel.length > 0 ? <span className="wau-sec wau-num">{S.seleccionadas(sel.length)}</span> : null}
					{veWa && studentIds.length > 0 ? <button type="button" className="wau-btn p" disabled={busy} onClick={() => setMasivo(true)}>{S.whatsappVarias(studentIds.length)}</button> : null}
					<button type="button" className="wau-btn s" disabled={busy || vigentes.length === 0} onClick={pdfTodas}>{S.pdfTodas}</button>
				</div>
			</div>
			{movil ? (
				<div>
					{vigentes.length === 0 ? <p className="wau-sec" style={{ margin: 0 }}>{S.sinAsistentes}</p> : vigentes.map(a => (
						<div key={a.id} className="wau-tarjeta">
							<label style={{ display: "flex", gap: 8, alignItems: "center", margin: 0, position: "static" }}>
								<input type="checkbox" disabled={!a.studentId} checked={sel.includes(a.id)} aria-label={a.name}
									onChange={() => setSel(s => s.includes(a.id) ? s.filter(x => x !== a.id) : [...s, a.id])} />
								<b>{a.name}</b></label>
							<div className="linea">{a.kindLabel}{a.hostName ? " · " + S.invitaA(a.hostName) : a.identityCode ? " · " + a.identityCode : ""}{a.tableNo ? " · " + S.colMesa + " " + a.tableNo : ""}</div>
							<div className="wau-acciones" style={{ justifyContent: "flex-start", flexWrap: "wrap", marginTop: 8 }}>{whatsapp(a)}{enlace(a)}</div>
						</div>
					))}
				</div>
			) : (
				<div className="wau-tabla">
					<table>
						<thead><tr>
							<th><input type="checkbox" checked={todas} disabled={conAlumna.length === 0} aria-label={S.elegTodas} onChange={() => setSel(todas ? [] : conAlumna.map(a => a.id))} /></th>
							<th>{S.colNombre}</th><th>{S.colTipo}</th><th>{S.colMesa}</th><th>{S.colWhatsapp}</th><th>{S.colEnlace}</th>
						</tr></thead>
						<tbody>
							{vigentes.length === 0 ? <tr><td colSpan={6} className="wau-sec" style={{ textAlign: "center", padding: 18 }}>{S.sinAsistentes}</td></tr> : vigentes.map(a => (
								<tr key={a.id} className={sel.includes(a.id) ? "sel" : ""}>
									<td><input type="checkbox" disabled={!a.studentId} checked={sel.includes(a.id)} aria-label={a.name}
										onChange={() => setSel(s => s.includes(a.id) ? s.filter(x => x !== a.id) : [...s, a.id])} /></td>
									<td><b>{a.name}</b><div className="wau-sec" style={{ fontSize: 12.5 }}>{a.hostName ? S.invitaA(a.hostName) : a.identityCode || ""}</div></td>
									<td style={{ whiteSpace: "nowrap" }}>{a.kindLabel}{a.isChild ? " · " + S.esNino : ""}</td>
									<td className="wau-num">{a.tableNo ?? "—"}</td>
									<td style={{ whiteSpace: "nowrap" }}>{whatsapp(a)}</td>
									<td style={{ whiteSpace: "nowrap" }}>{enlace(a)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{masivo ? <EnvioMasivo campusId={campusId} studentIds={studentIds} origen="evento" referencia={evento.name || null} onClose={() => setMasivo(false)} /> : null}
		</div>
		</>
	);
};

export default InvitacionesTab;
